import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CentresShimmer } from '../components/shimmer';
import { useListCentresQuery } from '../store/siteContentApi';
import { ROUTES } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';
import heroImg from '../assets/img/v5/about-centre.jpg';

// Branches — built to the v5 reference.
//
// A hero with a search box, a row of filter chips, then the branch list
// beside a sticky detail panel, and the "Inside a Care Centre" band.
//
// Three things the reference shows that the /centres records do not
// carry, and what happens to each:
//   · a region (Telangana, Andhra Pradesh …) — the records only have a
//     city, so the chips are cities. Thirteen of them, which still wraps
//     to two rows at desktop.
//   · a type pill (Central lab / Care Centre) — no field, so the pill
//     shows the city instead, which is real and still useful.
//   · service chips and a per-branch Google rating — no fields at all.
// All three are in API-NOTES.

const INSIDE = [
  ['Reclining collection chair', 'Comfortable for elderly patients and anyone who feels faint.'],
  ['Samples kept chilled', 'Refrigerated storage until pickup to the central lab.'],
  ['Safe disposal', 'Separate biohazard bins and single-use needles, every time.'],
  ['Results on WhatsApp', 'No need to come back to collect your report.'],
];

// Great-circle distance in km. Only used for the two records that
// currently carry coordinates; the rest sort after them untouched.
const distanceKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const Centres = () => {
  const { data: centres = [], isLoading } = useListCentresQuery();
  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = useState('');
  const [city, setCity] = useState(searchParams.get('city') || 'All');
  const [selected, setSelected] = useState(null);
  const [userLoc, setUserLoc] = useState(null);
  const [locStatus, setLocStatus] = useState('idle');

  // Keep the chip in step with ?city=, which the City page and the
  // detail panel both link with.
  useEffect(() => {
    const c = searchParams.get('city');
    if (c && c !== city) setCity(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const requestLocation = () => {
    if (!navigator.geolocation) { setLocStatus('error'); return; }
    setLocStatus('pending');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocStatus('granted');
      },
      (err) => setLocStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'error'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  };

  const cities = useMemo(
    () => ['All', ...Array.from(new Set(centres.map((c) => c.city).filter(Boolean)))],
    [centres],
  );

  // Only offer "Use my location" if at least one branch can actually be
  // measured against it — otherwise the button would do nothing.
  const anyGeocoded = centres.some((c) => c.lat != null && c.lng != null);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = centres
      .filter((c) => (city === 'All' || c.city === city))
      .filter((c) => !q || `${c.name} ${c.city} ${c.addr}`.toLowerCase().includes(q))
      .map((c) => ({
        ...c,
        km: userLoc && c.lat != null && c.lng != null
          ? distanceKm(userLoc, { lat: c.lat, lng: c.lng })
          : null,
      }));
    if (!userLoc) return list;
    // Measured branches first, nearest to furthest; the rest keep their
    // admin order behind them rather than being hidden.
    return [...list].sort((a, b) => {
      if (a.km == null && b.km == null) return 0;
      if (a.km == null) return 1;
      if (b.km == null) return -1;
      return a.km - b.km;
    });
  }, [centres, city, query, userLoc]);

  const active = shown.find((c) => c._id === selected) || shown[0] || null;

  const pickCity = (c) => {
    setCity(c);
    setSelected(null);
    setSearchParams(c === 'All' ? {} : { city: c }, { replace: true });
  };

  return (
    <Box>
      {/* ── hero ─────────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            minHeight: { xs: 360, md: 460 },
            display: 'flex',
            alignItems: 'end',
            borderRadius: '36px',
            overflow: 'hidden',
            bgcolor: 'var(--tl-g9)',
          }}
        >
          <Box
            component="img"
            src={heroImg}
            alt=""
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(90deg, rgba(7,33,29,0.92) 0%, rgba(7,33,29,0.72) 50%, rgba(7,33,29,0.2) 100%)',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 880,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.25,
              p: 'clamp(28px, 5vw, 56px)',
              color: '#FFFFFF',
            }}
          >
            <Box
              component="span"
              sx={{
                alignSelf: 'start',
                px: '10px',
                py: '6px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {centres.length ? `${centres.length} branches across India` : 'Branches across India'}
            </Box>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(32px, 5.4vw, 64px)',
                lineHeight: 0.95,
                letterSpacing: '-.035em',
                textWrap: 'balance',
                color: '#FFFFFF',
              }}
            >
              Find a TrustLab Care Centre near you.
            </Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: '#D5E7E2', maxWidth: 600 }}>
              Walk in for a quick, comfortable sample collection, or book a Trustian to come home.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                p: 0.75,
                borderRadius: '18px',
                bgcolor: '#FFFFFF',
                maxWidth: 560,
              }}
            >
              <Box
                component="input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="City, area or pincode"
                aria-label="Search branches"
                sx={{
                  flex: 1,
                  minWidth: 180,
                  border: 0,
                  outline: 0,
                  bgcolor: 'transparent',
                  color: 'var(--tl-g9)',
                  fontFamily: UI,
                  fontSize: 16,
                  p: '12px 14px',
                }}
              />
              {anyGeocoded && (
                <Box
                  component="button"
                  type="button"
                  onClick={requestLocation}
                  sx={{
                    border: 0,
                    cursor: 'pointer',
                    borderRadius: '14px',
                    p: '12px 16px',
                    bgcolor: 'var(--tl-g9)',
                    color: '#FFFFFF',
                    fontFamily: UI,
                    fontSize: 15,
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {locStatus === 'pending' ? 'Locating…' : locStatus === 'granted' ? 'Nearest first' : 'Use my location'}
                </Box>
              )}
            </Box>
            {(locStatus === 'denied' || locStatus === 'error') && (
              <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-yellow)' }}>
                We couldn&rsquo;t read your location. Search by city or pincode instead.
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box component="main" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9 }}>
        {/* ── city chips ─────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, p: '8px 4px 0' }}>
          {cities.map((c) => {
            const on = c === city;
            return (
              <Box
                key={c}
                component="button"
                type="button"
                onClick={() => pickCity(c)}
                aria-pressed={on}
                sx={{
                  border: 0,
                  cursor: 'pointer',
                  borderRadius: '999px',
                  p: '10px 16px',
                  whiteSpace: 'nowrap',
                  fontFamily: UI,
                  fontSize: 14,
                  fontWeight: 700,
                  transition: `background-color .25s ${EASE}`,
                  bgcolor: on ? 'var(--tl-g9)' : '#FFFFFF',
                  color: on ? '#FFFFFF' : 'var(--tl-g9)',
                }}
              >
                {c}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mt: 1.5 }}>
          {/* ── the list ─────────────────────────────────────────────── */}
          <Box sx={{ flex: '1 1 380px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {isLoading ? (
              <CentresShimmer />
            ) : (
              <>
                <Box component="span" sx={{ px: 1, fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                  {`${shown.length} location${shown.length === 1 ? '' : 's'}`}
                </Box>

                {shown.map((b) => {
                  const on = active && b._id === active._id;
                  return (
                    <Box
                      key={b._id}
                      component="button"
                      type="button"
                      onClick={() => setSelected(b._id)}
                      aria-pressed={!!on}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                        p: '20px',
                        borderRadius: '24px',
                        bgcolor: '#FFFFFF',
                        color: 'var(--tl-g9)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        border: `2px solid ${on ? 'var(--tl-teal)' : '#FFFFFF'}`,
                        transition: `border-color .25s ${EASE}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1.25 }}>
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 18, fontWeight: 800 }}>{b.name}</Box>
                        {/* Most records name the branch after its city, so
                            the pill would just repeat the heading. Show it
                            only where it actually adds something — the two
                            Hyderabad branches, and anywhere the admin gives
                            a branch its own name. */}
                        {b.city && b.city !== b.name && (
                          <Box
                            component="span"
                            sx={{
                              px: '10px',
                              py: '5px',
                              borderRadius: '999px',
                              bgcolor: 'var(--tl-mint)',
                              color: 'var(--tl-teal-d)',
                              fontFamily: UI,
                              fontSize: 12,
                              fontWeight: 800,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {b.city}
                          </Box>
                        )}
                      </Box>
                      <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: 'var(--tl-muted)' }}>
                        {b.addr}
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        {b.hours && (
                          <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700 }}>{b.hours}</Box>
                        )}
                        {b.km != null && (
                          <Box
                            component="span"
                            sx={{
                              px: '9px',
                              py: '5px',
                              borderRadius: '999px',
                              bgcolor: 'var(--tl-mint)',
                              color: 'var(--tl-teal-d)',
                              fontFamily: UI,
                              fontSize: 12,
                              fontWeight: 800,
                            }}
                          >
                            {b.km < 1 ? 'Under 1 km' : `${b.km.toFixed(1)} km away`}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}

                {shown.length === 0 && (
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: '24px',
                      bgcolor: '#FFFFFF',
                      textAlign: 'center',
                      fontFamily: UI,
                      color: 'var(--tl-muted)',
                    }}
                  >
                    No branches match that. Try another city, or book a home collection.
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* ── detail panel ─────────────────────────────────────────── */}
          <Box
            sx={{
              flex: '1.3 1 420px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              position: { md: 'sticky' },
              top: { md: 100 },
            }}
          >
            <Box
              component="section"
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-end',
                minHeight: 420,
                p: 2,
                borderRadius: '28px',
                overflow: 'hidden',
                // The reference draws a plotting grid behind the card
                // rather than a live map. Ours does the same: the Maps
                // JavaScript API is not billable on the project yet, so
                // an embed would render the "for development only"
                // watermark. Directions still open in Google Maps.
                bgcolor: '#D5EFE8',
                backgroundImage:
                  'linear-gradient(#C3E4DA 1px, transparent 1px), linear-gradient(90deg, #C3E4DA 1px, transparent 1px)',
                backgroundSize: '48px 48px',
              }}
            >
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  px: '10px',
                  py: '6px',
                  borderRadius: '999px',
                  bgcolor: '#FFFFFF',
                  color: 'var(--tl-teal-d)',
                  fontFamily: UI,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Map coming soon
              </Box>

              {active && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    p: 2.5,
                    borderRadius: '22px',
                    bgcolor: '#FFFFFF',
                    boxShadow: '0 18px 40px -20px rgba(11,43,39,0.4)',
                  }}
                >
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 19, fontWeight: 800 }}>{active.name}</Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted)' }}>
                    {[active.addr, active.hours].filter(Boolean).join(' · ')}
                  </Box>
                  <Box
                    component={Link}
                    to={`${ROUTES.CITY}?city=${encodeURIComponent(active.city || '')}`}
                    sx={{
                      fontFamily: UI,
                      fontSize: 14,
                      fontWeight: 800,
                      color: 'var(--tl-teal-d)',
                      textDecoration: 'underline',
                      width: 'fit-content',
                    }}
                  >
                    All tests and branches in this city →
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                    {[
                      { t: 'Directions', href: active.maps_url, bg: 'var(--tl-g9)', fg: '#FFFFFF', ext: true },
                      { t: 'Call', href: active.phone ? `tel:${String(active.phone).replace(/\s/g, '')}` : null, bg: 'var(--tl-mint)', fg: 'var(--tl-g9)' },
                      { t: 'Book', to: ROUTES.BOOKING, bg: 'var(--tl-yellow)', fg: 'var(--tl-g9)' },
                    ].filter((a) => a.to || a.href).map((a) => (
                      <Box
                        key={a.t}
                        {...(a.to ? { component: Link, to: a.to } : { component: 'a', href: a.href })}
                        {...(a.ext ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
                        sx={{
                          display: 'inline-flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: '12px 8px',
                          borderRadius: '16px',
                          bgcolor: a.bg,
                          color: a.fg,
                          fontFamily: UI,
                          fontSize: 14,
                          fontWeight: 800,
                          textDecoration: 'none',
                          '&:hover': { color: a.fg, filter: 'brightness(1.07)' },
                        }}
                      >
                        {a.t}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* ── inside a Care Centre ───────────────────────────────────── */}
        <Box
          component="section"
          sx={{ display: 'flex', flexWrap: 'wrap', mt: 7.5, borderRadius: '32px', overflow: 'hidden', bgcolor: '#FFFFFF' }}
        >
          <Box
            component="img"
            src={heroImg}
            alt="Inside a TrustLab Care Centre"
            sx={{ flex: '1 1 420px', minWidth: 0, width: '100%', maxHeight: 520, objectFit: 'cover', display: 'block' }}
          />
          <Box
            sx={{
              flex: '1 1 380px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2.25,
              p: 'clamp(24px, 4vw, 48px)',
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: UI,
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'var(--tl-teal-d)',
              }}
            >
              Inside a Care Centre
            </Box>
            <Typography
              component="h2"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(24px, 3vw, 36px)',
                lineHeight: 1.05,
                letterSpacing: '-.02em',
                color: 'var(--tl-g9)',
              }}
            >
              Clean, calm and quick.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {INSIDE.map(([t, d]) => (
                <Box key={t} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 28,
                      height: 28,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: 'var(--tl-teal)',
                      color: '#FFFFFF',
                      fontFamily: UI,
                      fontSize: 13,
                      fontWeight: 800,
                    }}
                  >
                    ✓
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>{t}</Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
                      {d}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Centres;
